import type { FC, ReactNode } from 'react';
import React from 'react';

import { ChevronDownIcon, IconExternal } from '../../../assets/icons/icons';
import type { AnalogSpec, SpecStatus, SpecSummary } from '../../../types/results';
import { cx } from '../../../utils/cx';
import styles from './AnalogCompare.module.scss';

/* ─── статус характеристики ─── */

const STATUS_LABEL: Record<SpecStatus, string> = {
    ok: 'Совпадает',
    diff: 'Расхождение',
    na: 'Нет данных',
};

const plural = (n: number, [one, few, many]: [string, string, string]): string => {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return one;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
    return many;
};

/* Порядок и вид счётчиков в шапке таблицы. Нулевые не показываем. */
const COUNTERS: { status: SpecStatus; text: (n: number) => string; icon: ReactNode }[] = [
    {
        status: 'ok',
        text: (n) => `${n} ${plural(n, ['совпадает', 'совпадают', 'совпадают'])}`,
        icon: <path d="M5 12.5l4.5 4.5L19 7.5" />,
    },
    {
        status: 'diff',
        text: (n) => `${n} ${plural(n, ['расхождение', 'расхождения', 'расхождений'])}`,
        icon: <path d="M5 9h14M5 15h14M15 5L9 19" />,
    },
    {
        status: 'na',
        text: (n) => `${n} нет данных`,
        icon: <path d="M6 12h12" />,
    },
];

const EMPTY_SUMMARY: SpecSummary = { ok: 0, diff: 0, na: 0 };

/** Есть ли что показывать в блоке сравнения. */
export const hasSpecs = (specs?: AnalogSpec[]): boolean => Boolean(specs?.length);

const hostOf = (url: string): string => {
    try {
        return new URL(url).hostname.replace(/^www\./, '');
    } catch {
        return url;
    }
};

const SourceLink: FC<{ url?: string }> = ({ url }) =>
    url ? (
        <a className={styles.src} href={url} target="_blank" rel="noreferrer noopener">
            {hostOf(url)}
            <IconExternal />
        </a>
    ) : null;

/* ─── бейдж соответствия = кнопка раскрытия ─── */

interface MatchToggleProps {
    pct: number;
    tone: 'good' | 'mid' | 'bad';
    className?: string;
    open: boolean;
    controls: string;
    onToggle: () => void;
}

export const MatchToggle: FC<MatchToggleProps> = ({ pct, tone, className, open, controls, onToggle }) => (
    <button
        type="button"
        className={cx(className, styles.matchBtn)}
        data-tone={tone}
        aria-expanded={open}
        aria-controls={controls}
        aria-label={`Соответствие ${pct}%: сравнение характеристик`}
        onClick={onToggle}
    >
        {pct}%
        <span className={styles.chevron} aria-hidden="true">
            <ChevronDownIcon />
        </span>
    </button>
);

/** Класс для строки предложения, у которой раскрыто сравнение. */
export const openRowClass = styles.openRow;

/* ─── раскрывающаяся строка со сравнением ─── */

interface AnalogCompareRowProps {
    id: string;
    colSpan: number;
    specs: AnalogSpec[];
    /** Счётчики с бэка (characteristics_summary), пересчитывать не нужно. */
    summary?: SpecSummary;
    analogUrl?: string;
}

export const AnalogCompareRow: FC<AnalogCompareRowProps> = ({
    id, colSpan, specs, summary = EMPTY_SUMMARY, analogUrl,
}) => (
    <tr className={styles.row}>
        <td className={styles.cell} colSpan={colSpan}>
            <div id={id} className={styles.cmp}>
                <div className={styles.head}>
                    <h4>Сравнение характеристик</h4>
                    {COUNTERS.filter(({ status }) => summary[status] > 0).map(({ status, text, icon }) => (
                        <span key={status} className={cx(styles.cnt, styles[`cnt_${status}`])}>
                            <svg viewBox="0 0 24 24" aria-hidden="true">{icon}</svg>
                            {text(summary[status])}
                        </span>
                    ))}
                </div>

                {specs.length === 0 ? (
                    <p className={styles.empty}>Сравнение характеристик недоступно</p>
                ) : (
                    <>
                        <div className={cx(styles.grid, styles.th)}>
                            <span>Характеристика</span>
                            <span>Заявленная позиция</span>
                            <span>Аналог</span>
                            <span>Результат</span>
                        </div>

                        {/* Порядок строк — как пришёл с бэка: важные характеристики первыми. */}
                        {specs.map((spec, index) => (
                            <div
                                key={`${spec.name}-${index}`}
                                className={cx(
                                    styles.grid,
                                    styles.tr,
                                    spec.status !== 'diff' && styles[`tr_${spec.status}`],
                                )}
                            >
                                <span className={styles.key}>{spec.name}</span>
                                <div>
                                    <div className={styles.val}>{spec.base || '—'}</div>
                                </div>
                                <div>
                                    <div className={cx(styles.val, styles.valAnalog)}>
                                        {spec.analog.trim() ? spec.analog : '—'}
                                    </div>
                                    {/* {spec.status !== 'na' && <SourceLink url={spec.analogUrl} />} */}
                                    {spec.status !== 'na' && <SourceLink url={analogUrl} />}
                                </div>
                                <span className={cx(styles.st, styles[`st_${spec.status}`])}>
                                    {STATUS_LABEL[spec.status]}
                                </span>
                            </div>
                        ))}
                    </>
                )}

                <div className={styles.foot}>
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 11v5M12 8h.01" />
                    </svg>
                    Ссылка под значением открывает страницу аналога. Аналог не заменяет заявленную позицию автоматически.
                </div>
            </div>
        </td>
    </tr>
);